import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import PropertySearchInput from './PropertySearchInput';
import CcEmailsInput from './CcEmailsInput';
import { API_URL } from '../config/api';
import { formatCurrency } from '../utils/formatters';
import { Info, Bell } from 'lucide-react';
import './AddPortfolioPropertyModal.css';

const AddPortfolioPropertyModal = ({
    isOpen,
    onClose,
    onSuccess,
    editMode = false,
    initialData = null,
}) => {
    const { session } = useAuth();
    const { showToast } = useToast();

    const [selectedProperty, setSelectedProperty] = useState(initialData ? {
        property_id: initialData.property_id,
        address: initialData.address,
        beds: initialData.beds,
        baths: initialData.baths,
        sqft: initialData.sqft,
        list_price: initialData.list_price,
        primary_photo_url: initialData.primary_photo_url,
    } : null);

    const [alertsEnabled, setAlertsEnabled] = useState(initialData?.alerts_enabled ?? true);
    const [arvTarget, setArvTarget] = useState(initialData?.arv_target_value ?? '');
    const [arvAboveList, setArvAboveList] = useState(initialData?.arv_above_list_amount ?? '');
    const [arvAbovePct, setArvAbovePct] = useState(initialData?.arv_above_value_pct ?? '');
    const [alertCcEmails, setAlertCcEmails] = useState(initialData?.alert_cc_emails ?? []);
    const [notes, setNotes] = useState(initialData?.notes || '');
    const [saving, setSaving] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        if (!selectedProperty) return;
        setSaving(true);
        const alerts = {
            enabled: alertsEnabled,
            arv_target_value: arvTarget ? Number(arvTarget) : null,
            arv_above_list_amount: arvAboveList ? Number(arvAboveList) : null,
            arv_above_value_pct: arvAbovePct ? Number(arvAbovePct) : null,
            cc_emails: alertCcEmails,
        };
        const body = editMode
            ? { notes: notes || null, alerts }
            : { property_id: selectedProperty.property_id, notes: notes || null, alerts };

        try {
            const url = editMode && initialData
                ? `${API_URL}/portfolio/${initialData.id}`
                : `${API_URL}/portfolio`;
            const method = editMode ? 'PATCH' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session?.access_token}`,
                },
                body: JSON.stringify(body),
            });

            if (res.ok) {
                showToast(editMode ? 'Alerts updated' : 'Added to portfolio!', 'success');
                onSuccess?.();
            } else if (res.status === 409) {
                showToast('Property already in portfolio', 'error');
            } else {
                const err = await res.json().catch(() => ({}));
                showToast(err.detail || 'Failed to save', 'error');
            }
        } catch {
            showToast('Network error', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="portfolio-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 className="modal-title">{editMode ? 'Edit Alerts' : 'New Property'}</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-content-scroll">
                    <div className="pm-step-content">
                        {/* Property search (add mode only) */}
                        {!editMode && (
                            <div className="form-group full-width">
                                <label className="form-label">Search Property</label>
                                <PropertySearchInput
                                    placeholder="Type an address to search..."
                                    onSelect={(data) => {
                                        setSelectedProperty({
                                            property_id: data.property_id,
                                            address: data.address,
                                            beds: data.beds,
                                            baths: data.baths,
                                            sqft: data.sqft,
                                            list_price: data.list_price,
                                            primary_photo_url: data.primary_photo_url,
                                        });
                                    }}
                                    onClear={() => setSelectedProperty(null)}
                                />
                            </div>
                        )}

                        {/* Property preview */}
                        {selectedProperty && (
                            <div className="pm-property-preview">
                                {selectedProperty.primary_photo_url && (
                                    <img
                                        src={selectedProperty.primary_photo_url}
                                        alt={selectedProperty.address}
                                        className="pm-preview-img"
                                    />
                                )}
                                <div className="pm-preview-info">
                                    <div className="pm-preview-address">{selectedProperty.address}</div>
                                    <div className="pm-preview-details">
                                        {selectedProperty.beds || '-'} bd · {selectedProperty.baths || '-'} ba · {selectedProperty.sqft ? `${Number(selectedProperty.sqft).toLocaleString()} sqft` : '-'}
                                    </div>
                                    <div className="pm-preview-price">{formatCurrency(selectedProperty.list_price)}</div>
                                </div>
                            </div>
                        )}

                        {/* ARV Alerts */}
                        <div className="form-group full-width notification-section" style={{ marginTop: 16 }}>
                            <div className="notification-section-header">
                                <Bell size={16} />
                                <span>ARV Alerts</span>
                            </div>
                            <p className="notification-hint" style={{ margin: '0 0 12px 0' }}>
                                <Info size={13} />
                                Get notified when the estimated ARV crosses your thresholds.
                            </p>

                            <label className="checkbox-label" style={{ marginBottom: alertsEnabled ? 12 : 0 }}>
                                <input
                                    type="checkbox"
                                    checked={alertsEnabled}
                                    onChange={(e) => setAlertsEnabled(e.target.checked)}
                                />
                                Enable alerts for this property
                            </label>

                            {alertsEnabled && (
                                <>
                                    <div className="pm-alert-fields">
                                        <div className="form-group">
                                            <label className="form-label">Alert when ARV reaches ($)</label>
                                            <input
                                                type="number"
                                                className="std-input"
                                                placeholder="e.g. 250000"
                                                value={arvTarget}
                                                onChange={(e) => setArvTarget(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">ARV exceeds list price by ($)</label>
                                            <input
                                                type="number"
                                                className="std-input"
                                                placeholder="e.g. 50000"
                                                value={arvAboveList}
                                                onChange={(e) => setArvAboveList(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">ARV exceeds value by (%)</label>
                                            <input
                                                type="number"
                                                className="std-input"
                                                placeholder="e.g. 20"
                                                value={arvAbovePct}
                                                onChange={(e) => setArvAbovePct(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {/* Alert CC emails */}
                                    <div className="form-group" style={{ marginTop: 12 }}>
                                        <CcEmailsInput
                                            label="CC Emails (for alerts on this property)"
                                            placeholder="partner@example.com"
                                            value={alertCcEmails}
                                            onChange={setAlertCcEmails}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Notes */}
                        <div className="form-group full-width" style={{ marginTop: 16 }}>
                            <label className="form-label">Notes (optional)</label>
                            <textarea
                                className="std-input pm-notes"
                                placeholder="Any notes about this property..."
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button
                        className="btn-primary"
                        onClick={handleSubmit}
                        disabled={saving || !selectedProperty}
                    >
                        {saving ? 'Saving...' : editMode ? 'Save Changes' : 'Add Property'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddPortfolioPropertyModal;
